import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CfInfoDialog from "@/components/pop-ui/cf-info-dialog";
import { toast } from "@/components/pop-ui/pop-toast";
import { PasswordInput } from "@/components/ui/password-input";
import { authClient } from "@/lib/auth/auth-client";
import { handleAuthCall } from "@/lib/auth/auth-utils";
import { getAuthHeadersWaitSign } from "@/lib/auth/td-utils";
import { useGlobalConfig } from "@/providers/global-config-context";
import { useTurnstile } from "@/providers/turnstile-provider";
import { trackLogin } from "@/track/gtm";
import { Button } from "../components/form-items";
import { handleAuthApiError, type UserFormErrors, validatePassword } from "../utils";

// 邮箱登录组件
export function LoginFormEmail({
  email,
  area,
  onResetPassword,
  onFinish
}: {
  email: string;
  area: string;
  onResetPassword: () => void;
  onFinish: () => void;
}) {
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<UserFormErrors>({
    password: ""
  });
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    t
  } = useTranslation();
  const {
    globalConfig
  } = useGlobalConfig();
  const {
    isSdkReady,
    sdkError
  } = useTurnstile();

  // 获取当前session以获取用户ID

  // 登录 mutation
  const {
    mutateAsync: loginWithEmail,
    isPending: isLoggingInWithEmail
  } = useMutation({
    mutationFn: async (cfTurnstileToken: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set 30-second timeout to reset button
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        toast.error(t("toast.error.requestTimeout", "Request timeout"));
      }, 30000);
      try {
        // 获取包含 TD 参数的请求头（等待 sign 准备就绪）
        const requestHeaders = await getAuthHeadersWaitSign("email-login", {
          "X-AREA": area,
          // 使用传入的 area 参数
          ...(cfTurnstileToken && {
            "x-captcha-response": cfTurnstileToken
          })
        });
        const result = await handleAuthCall(authClient.signIn.email({
          email,
          password,
          fetchOptions: {
            headers: requestHeaders
          }
        }));

        // Clear timeout on success
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return result;
      } catch (error) {
        // Clear timeout on error
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        throw error;
      }
    },
    onSuccess: (data: any) => {
      setIsTurnstileLoading(false); // Clear loading state on success
      // Don't reset process state on success - page will redirect anyway
      onFinish();
      // 登录成功埋点
      trackLogin({
        pop_user_id: data?.session?.userId,
        // 从登录响应中获取用户ID
        status: "success"
      });
    },
    onError: (error_0: any) => {
      setIsTurnstileLoading(false); // Clear loading state on error
      setIsTurnstileProcessStarted(false); // Reset process state on error
      handleAuthApiError(error_0, t("toast.error.loginFailedTryLater"));
      // 登录失败埋点
      trackLogin({
        pop_user_id: "",
        status: "failed"
      });
      // Ensure button state is reset after any error (including blacklist errors)
      setCaptchaOpen(false);
    }
  });

  // Dialog open state for captcha (closed by default, opens when login is attempted)
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false);
  const [isTurnstileProcessStarted, setIsTurnstileProcessStarted] = useState(false);

  // Handle Turnstile completion - use token immediately and clear it
  const handleTurnstileSuccess = (token: string) => {
    setCaptchaOpen(false);
    // Don't clear loading state here - let the API call handle it
    // Use token immediately and clear it
    loginWithEmail(token);
  };

  // Handle turnstile dialog open - set loading state
  const handleTurnstileOpen = () => {
    // Check SDK readiness before opening dialog
    if (sdkError) {
      // SDK failed to load, show error and don't open dialog
      toast.error(t("gateway.error.475"));
      return;
    }
    if (!isSdkReady) {
      // SDK is still loading, do nothing (user can try again)
      return;
    }
    setIsTurnstileLoading(true);
    setIsTurnstileProcessStarted(true);
    setCaptchaOpen(true);
  };

  // Handle turnstile error - reset loading state
  const handleTurnstileError = () => {
    setIsTurnstileLoading(false);
    setIsTurnstileProcessStarted(false);
    setCaptchaOpen(false);
    // Show error message as toast for dialog verification failure
    toast.error(t("gateway.error.476"));
  };

  // 登录处理
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    // Prevent re-submission if turnstile process has already started
    if (isTurnstileProcessStarted) {
      return;
    }

    // Reset timeout state when starting new login attempt
    setIsTimedOut(false);
    setErrors({
      password: ""
    });
    const passwordError = validatePassword(password, t);
    if (passwordError) {
      setErrors({
        password: passwordError
      });
      return;
    }

    // If Turnstile is globally disabled, proceed directly with login
    if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
      loginWithEmail("");
      return;
    }

    // Open captcha dialog - the dialog will handle turnstile logic internally
    handleTurnstileOpen();
  };

  // 密码失去焦点时校验
  const handlePasswordBlur = () => {
    const passwordError_0 = validatePassword(password, t);
    setErrors(prev => ({
      ...prev,
      password: passwordError_0 || ""
    }));
  };
  const isPending = isLoggingInWithEmail;
  const isTurnstileEnabled = globalConfig?.PUBLIC_TURNSTILE_ENABLED;

  // Determine if button should be in loading state
  const isButtonLoading = isPending && !isTimedOut || isTurnstileEnabled && isTurnstileLoading;
  return <form onSubmit={handleLogin} className="space-y-3">
			<div className="text-foreground flex min-h-10 items-center bg-gray-100 px-3 py-2 text-xs! md:text-base! break-all opacity-50 md:min-h-12">
				<span>{email}</span>
			</div>

			<PasswordInput id="password" value={password} onChange={e_0 => setPassword(e_0.target.value)} onBlur={handlePasswordBlur} disabled={isPending} error={errors.password} placeholder={t("placeholder.enterPassword")} />

			<CfInfoDialog open={captchaOpen} onOpenChange={setCaptchaOpen} containerId="cf-turnstile-container-login-dialog" onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={handleTurnstileError} />

			<Button type="submit" className="w-full" size="lg" loading={isButtonLoading}>
				{isButtonLoading ? t("button.loginInProgress") : t("button.login")}
			</Button>

			<div className="flex justify-center text-sm">
				<Button type="button" onClick={() => onResetPassword()} variant="link" className="text-primary underline underline-offset-4 hover:no-underline">
					{t("button.forgotPassword")}
				</Button>
			</div>
		</form>;
}